import { CurrencyIsoCode, OrderState } from "@/enums";
import {
  GlobalEInternationalSnapshot,
  OrderDraftAddress,
  OrderItemV2,
} from "@/models/orders/OrderDraft";

interface BoxSimple {
  packageId: string;
  items: string[];
  dangerousGoodsScore: number;
  weight: number;
}

export enum ShippingType {
  Physical = "Physical",
  Virtual = "Virtual",
  Mixed = "Mixed",
}

export enum GameItemState {
  Prepared = "Prepared",
  Purchased = "Purchased",
  Completed = "Completed",
  Refunded = "Refunded",
}

export interface GameItem extends EssentialGameItem {
  id: string;
  thumbnail: string;
  thumbnailHorizontal: string;
  name: string;
  price: number;
  discount: number;
  discountPercentile: number; // checkout ui 에서 필요
  compatibleDevices: string[]; // checkout ui 에서 필요
  state: GameItemState;
  bundled?: boolean; // 제품과 함께 번들로 구매된 게임 아이템인지 여부입니다. 2025년 12월부로 추가되었습니다.
}

export interface PayerDetail {
  email: string;
  firstName: string;
  lastName: string;
  fee: number;
}

export interface EssentialGameItem {
  gameResellId: string;
  platform: string;
  disableCountryList: string[]; // TODO: 제거 검토
}

export interface OrderTracking {
  shippingMillis: number;
  state: string;
  trackingNumber: string;
  trackingUrl: string;
  deliveredAt: number;
  courier: string;
  shippingBoxes: BoxSimple[];
}

export interface OrderModelV2 {
  id: string;
  orderId: string;
  currency: CurrencyIsoCode;
  items: OrderItemV2[];
  gameItems: GameItem[];
  shippingAddress: OrderDraftAddress;
  createTime: number;
  updateTime: number;
  referralCode: string;
  couponCode?: string;
  shippingCountry: string;
  subtotalAmount: number;
  discountAmount: number;
  shippingPrice: number;
  totalAmount: number;
  paymentMethod: string;
  payerDetail: PayerDetail;
  uid: string;
  paygateTransactionId: string;
  estimateShippingTime: number;
  orderState: OrderState;

  /**
   * 철회 접수 시각 — **초 단위** epoch (mgmt#1215). Art. 11a §5 의 법정 기록값.
   *
   * ⚠️ 같은 응답의 `createTime`/`updateTime` 은 **밀리초**다. `* 1000` 없이 `new Date()` 에
   * 넣으면 법정 기록 자리에 1970 년이 찍힌다.
   *
   * ⚠️ `0` = 해당 없음. 이 기능이 아닌 경로(admin·Zapier 등)로 취소된 주문은 접수 레코드가
   * 없어 계속 0 이므로, "취소됨 + 시각 없음" 케이스를 화면에서 처리해야 한다. 서버가 임의
   * 시각으로 채우지 않는 이유는 §5 가 "확정 클릭 시각"을 요구하기 때문이다.
   */
  canceledAtEpochSecond?: number;
  /**
   * 취소 확정 환불액 (mgmt#1215).
   *
   * ⚠️ **`0` 을 "해당 없음" 으로 읽지 말 것.** 전액 쿠폰·포인트 결제처럼 환불이 실제로
   * 0 인 주문이 있어서 sentinel 로 쓸 수 없다. 접수 레코드의 존재 여부는 **위
   * `canceledAtEpochSecond` 하나로** 판정한다 — 두 필드는 mgmt#1215 가 함께 쓰므로,
   * 시각이 있으면 이 값도 유효하고(0 포함) 시각이 없으면 이 값도 의미가 없다.
   */
  refundAmount?: number;
  tracking: OrderTracking;
  /** (표시상) 제품가에 세금·관세·수수료 선포함(=US), 배송비만 별도. 회계 진실은 총액 — 분해는 mgmt 주문확정에서 재계산(presentation≠accounting). 이름 오해 주의 — docs/pricing-tiers.md §2/§6 */
  includeShippingAndTax: boolean;
  /**
   * GE 주문의 구매자 통화 결제 스냅샷 — 드래프트에서 승계(#5869, mgmt#1291). 공개 주문 조회 DTO
   * (`OrderV2PublicDto`)에도 같은 이름으로 나온다. 의미·`null` 규칙은 `OrderDraft.globalEInternational`.
   * mgmt#1291 이전 주문·비-GE 주문은 키가 없다.
   */
  globalEInternational?: GlobalEInternationalSnapshot;
}
